<script setup lang="ts">
export interface AssignedSensor {
  selectedSensor: string
  selectedMachinery: string
}

const props = defineProps<{
  sensorInfo: AssignedSensor
}>()

const emit = defineEmits<{
  (e: 'submit', assignedSensor: AssignedSensor): void
  (e: 'close'): void
}>()

const clonedSensorInfo = ref<AssignedSensor>({
  selectedMachinery: props.sensorInfo.selectedMachinery,
  selectedSensor: props.sensorInfo.selectedSensor,
})

const { trackerSensor: queryTrackerSensor, trackerSensorConfiguration: queryTrackerSensorConfiguration } = useQuery()

const { data: trackerSensors, isLoading: isLoadingTrackerSensorOptions } = queryTrackerSensor.getTrackerSensorOptions(props.sensorInfo.selectedSensor === '')
const { data: machineries, isLoading: isLoadingMachineryOptions } = queryTrackerSensor.getMachineryOptions(props.sensorInfo.selectedMachinery === '')

const currentlySelectedMachinery = computed(() => clonedSensorInfo.value.selectedMachinery)
const { data: trackerSensorConfig } = queryTrackerSensorConfiguration.getByMachineryId(currentlySelectedMachinery)

const trackerSensorOptions = computed(() => {
  // When `getTrackerSensorOptions` query is disabled, only return the selected sensor as the option list
  if (!trackerSensors.value) {
    return [{ value: props.sensorInfo.selectedSensor, label: props.sensorInfo.selectedSensor }]
  }
  return trackerSensors.value
})

const machineryOptions = computed(() => {
  // When `getMachineryOptions` query is disabled, only return the selected sensor as the option list
  if (!machineries.value) {
    return [{ value: props.sensorInfo.selectedMachinery, label: props.sensorInfo.selectedMachinery }]
  }

  // Ensure the current selected machinery via QR scanner is included in the options
  const options = [...machineries.value]
  const currentMachinery = clonedSensorInfo.value.selectedMachinery
  if (currentMachinery && !options.some(option => option.value === currentMachinery)) {
    options.push({ value: currentMachinery, label: currentMachinery })
  }
  return options
})

const isLoading = computed(() => isLoadingTrackerSensorOptions.value || isLoadingMachineryOptions.value)

const showQRCodeScanner = ref(false)

function submitMachineryIdViaQrScanner(id: string) {
  const machineryId = id.split('/').pop() || ''
  clonedSensorInfo.value.selectedMachinery = machineryId
  showQRCodeScanner.value = false
}

function handleMachineryQrScannerButtonClick() {
  showQRCodeScanner.value = !showQRCodeScanner.value
}
</script>

<template>
  <ThePopup
    show
    :title="$t('trackerSensor.overviewPage.assignPopup.title')"
    @close="emit('close')"
  >
    <FormKit
      v-model="clonedSensorInfo"
      type="form"
      :submit-label="$t('button.save')"
      @submit="emit('submit', clonedSensorInfo)"
    >
      <FormKitAutocomplete
        name="selectedSensor"
        :label="$t('trackerSensor.overviewPage.assignPopup.selectedSensor.label')"
        :options="trackerSensorOptions"
        :disabled="sensorInfo.selectedSensor !== '' || isLoading"
      />

      <FormKitAutocomplete
        name="selectedMachinery"
        :label="$t('trackerSensor.overviewPage.assignPopup.selectedMachinery.label')"
        :options="machineryOptions"
        :disabled="sensorInfo.selectedMachinery !== '' || isLoading"
      />

      <n-button v-if="sensorInfo.selectedMachinery === ''" class="mb-4" @click="handleMachineryQrScannerButtonClick">
        <template #icon>
          <Icon name="material-symbols:qr-code-scanner" />
        </template>
        {{ $t('machinery.details.trackerSensor.scanMachinery') }}
      </n-button>

      <TheQRCodeScanner
        v-if="showQRCodeScanner && sensorInfo.selectedMachinery === ''"
        class="mb-4"
        @decode-qr-code="submitMachineryIdViaQrScanner"
      />
      <div v-if="trackerSensorConfig" class="pb-4">
        <span v-if="trackerSensorConfig.configuration">
          {{ $t('trackerSensor.overviewPage.assignPopup.machineryTypeWithSensorConfig.label', { trackerSensorConfig: trackerSensorConfig.configuration.title, machineryType: trackerSensorConfig.machineryType }) }}
        </span>
        <span v-else>
          {{ $t('trackerSensor.overviewPage.assignPopup.machineryTypeWithoutSensorConfig.label', { machineryType: trackerSensorConfig.machineryType }) }}
        </span>
      </div>
    </FormKit>
  </ThePopup>
</template>
